import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getCoreRowModel, useReactTable } from '@tanstack/react-table';
import {
  TooltipProvider,
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  DataTable,
  DataTableColumnHeader,
  TablePagination,
} from '@cargomatic/ui';
import { showToast } from 'stores/ToastStore';
import { BARCODE, shipmentDocTypesMap } from 'config/documents';
import { useGetDocuments } from 'utils/DocumentsHooks';
import { SignedDocumentLink } from 'components/modules/documents/SignedDocumentLink';
import { FileIcon } from 'components/modules/documents/FileIcon';
import MultiSelectDocumentType from 'components/site/multi-document-type-select';
import { sluggify } from 'utils/localization/utils';

const DEFAULT_PER_PAGE = 10;
const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

const getAllForeignKeysForShipment = (shipment) => {
  const invoiceIds =
    shipment.receivables?.map((receivable) => receivable.invoice_id) || [];

  const payoutIds =
    shipment.payables?.map((payable) => payable.payout_id) || [];

  return [shipment._id, ...invoiceIds, ...payoutIds].filter((key) => key);
};

const buildShipmentByForeignKey = (shipments) => {
  const map = new Map();
  shipments.forEach((shipment) => {
    getAllForeignKeysForShipment(shipment).forEach((key) => {
      if (!map.has(key)) {
        map.set(key, shipment);
      }
    });
  });
  return map;
};

export const ShipmentDocumentList = ({ shipments }) => {
  const { t } = useTranslation();
  const [filterDocumentTypes, setFilterDocumentTypes] = useState([]);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: DEFAULT_PER_PAGE,
  });

  const shipmentDocumentForeignKeys = useMemo(
    () => [
      ...new Set(
        shipments
          .map((shipment) => getAllForeignKeysForShipment(shipment))
          .flat()
          .filter((key) => key),
      ),
    ],
    [shipments],
  );

  const shipmentByForeignKey = useMemo(
    () => buildShipmentByForeignKey(shipments),
    [shipments],
  );

  useEffect(() => {
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  }, [filterDocumentTypes, pagination.pageSize]);

  const {
    data: shipmentDocumentsData,
    error: shipmentDocumentsError,
    loading,
  } = useGetDocuments({
    foreignKeys: shipmentDocumentForeignKeys,
    type: filterDocumentTypes.length > 0 ? filterDocumentTypes : undefined,
    options: {
      limit: pagination.pageSize,
      skip: pagination.pageIndex * pagination.pageSize,
      order: [{ field: 'createdAt', direction: 'desc' }],
    },
  });

  useEffect(() => {
    if (shipmentDocumentsError) {
      showToast(
        t('GET_DOCUMENT_ERROR'),
        t('GET_DOCUMENT_FAILED_WITH_ERROR', { error: shipmentDocumentsError }),
        'error',
      );
    }
  }, [shipmentDocumentsError]);

  const rows = useMemo(() => {
    const documents = shipmentDocumentsData?.documents || [];
    return documents
      .filter((document) => document.type !== BARCODE.value)
      .map((document) => {
        const shipment = (document.foreignKeys || [])
          .map((key) => shipmentByForeignKey.get(key))
          .find(Boolean);
        return { document, shipment };
      })
      .filter((row) => row.shipment);
  }, [shipmentDocumentsData, shipmentByForeignKey]);

  const total = shipmentDocumentsData?.count || 0;
  const pageCount = Math.max(1, Math.ceil(total / pagination.pageSize));

  const columns = useMemo(
    () => [
      {
        id: 'document',
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={t('DOCUMENT')} />
        ),
        cell: ({ row }) => {
          const { document } = row.original;
          return (
            <div className="flex items-center space-x-2">
              <FileIcon extension={document.key.split('.').pop()} />
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <SignedDocumentLink
                      documentKey={document.key}
                      className="block max-w-[300px] truncate text-left text-blue-600 hover:underline focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                    >
                      {document.name}
                    </SignedDocumentLink>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="max-w-md break-all">
                    {document.name}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          );
        },
        enableSorting: false,
      },
      {
        id: 'documentType',
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={t('DOCUMENT_TYPE')} />
        ),
        cell: ({ row }) => {
          const { document } = row.original;
          const config = shipmentDocTypesMap[document.type];
          const label = config
            ? t(config.localizationSlug || sluggify(document.type))
            : t(sluggify(document.type));
          return label;
        },
        enableSorting: false,
      },
      {
        id: 'shipment',
        header: ({ column }) => (
          <DataTableColumnHeader
            column={column}
            title={t('SHIPMENT', { count: 1 })}
          />
        ),
        cell: ({ row }) => {
          const { shipment } = row.original;
          return (
            <a
              href={`/shipments/${shipment._id}`}
              target="_blank"
              rel="noreferrer"
              className="text-primary"
            >
              {shipment.shipment_id}
            </a>
          );
        },
        enableSorting: false,
      },
    ],
    [t],
  );

  const table = useReactTable({
    data: rows,
    columns,
    state: { pagination },
    pageCount,
    manualPagination: true,
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
  });

  if (!shipments.length) {
    return <i>{t('NO_SHIPMENT_ATTACHED_TO_ORDER')}</i>;
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <MultiSelectDocumentType
          foreignKeys={shipmentDocumentForeignKeys}
          filterDocumentTypes={filterDocumentTypes}
          setFilterDocumentTypes={setFilterDocumentTypes}
          excludedDocTypes={[BARCODE.value]}
          includedDocTypes={[]}
          refetch={false}
          setRefetch={() => {}}
        />
      </div>
      <div className="overflow-x-auto">
        <DataTable
          table={table}
          isLoading={loading}
          emptyResultsText={t('NO_RESULTS_FOUND')}
        />
      </div>
      <TablePagination
        page={pagination.pageIndex + 1}
        pages={pageCount}
        total={total}
        perPage={pagination.pageSize}
        onPageChange={(p) => table.setPageIndex(p - 1)}
        pageSizeOptions={PAGE_SIZE_OPTIONS}
        onPageSizeChange={(size) => table.setPageSize(size)}
      />
    </div>
  );
};
